<template>
  <v-main>
    <!--Background-->
    <div class="login-background">
      <video autoplay loop muted playsinline :poster="require('@/assets/img/3.png')">
        <source src="https://d2bt60lg5oycgc.cloudfront.net/30/driblab-login-intro-video.mp4" type="video/mp4">
      </video>
    </div>

    <!--Login form-->
    <v-container fluid class="fill-height pt-10">
     <v-row style="z-index: 1">
        <v-col cols="12" class="d-flex flex-column align-center justify-center">
          <!--Logo-->
          <v-img
            max-width="300px"
            class="white--text my-10"
            :src="require('@/assets/img/driblab_white_box_pro.png')"
          ></v-img>
        </v-col>
        <v-col cols="12" class="d-flex align-start justify-center flex-wrap">
          <!--Login card-->
          <MaterialCardLogin :color="$vuetify.theme.dark ? 'secondary' :  'brandDark'" class="mt-10" width="450px">
            <template v-slot:heading>
              <h2 class='white--text header-font' align="center">{{$t('login.login')}}</h2>
            </template>
            <v-container fluid class="">
              <v-form name="form" ref="form" lazy-validation @submit.prevent="handleLogin">
                <v-card-text class="">
                  <p class="body-1 font-weight-bold primary--text pb-1 mb-0 me-3">{{$t("login.username")}}</p>
                  <v-text-field outlined dense
                    v-model="user.username"
                    name="username"
                    :rules="emailRules"
                    prepend-inner-icon="person"
                    placeholder=" "
                    type="text"/>
                  <p class="body-1 font-weight-bold primary--text pb-1 mb-0 me-3">{{$t("login.password")}}</p>
                  <v-text-field outlined dense 
                    v-model="user.password"
                    id="password"
                    name="password"
                    :rules="passwordRules"
                    prepend-inner-icon="lock"
                    placeholder=" "
                    type="password"
                  />
                </v-card-text>
                <v-alert type="error" v-if="message" class="body-1">{{message}}</v-alert>
                <v-card-actions class="justify-center pa-1 py-xl-0 px-xl-0">
                  <v-btn
                    type="submit" rounded
                    :loading="loading"
                    class="elevation-0 monospace-font white--text"
                    :color="$vuetify.theme.dark ? 'secondary' : 'brandBlue'"
                    block
                  >{{ $t('login.enter') }}</v-btn>
                </v-card-actions>
                <v-spacer></v-spacer>
                <v-card-actions class="justify-center pt-3">
                  <v-btn
                    text small
                    class="justify-center subheader-font"
                    color="brandDark"
                    @click="onForgotPasswordClick()"
                  >{{ $t('login.forgotMyPassword') }}</v-btn>
                </v-card-actions>
              </v-form>
            </v-container>
          </MaterialCardLogin>
          <!--Announcements-->
          <v-card elevation="0" outlined class="my-10 pa-5" :style="{'margin-left': $vuetify.breakpoint.mdAndUp ? '80px' : ''}" width="450px">
            <v-card-title class="header-font primary--text pa-0 d-flex align-center" style="font-size: 22px;">
              <v-icon color="secondary" class="mb-2 me-1">volume_up</v-icon>
              {{ $t('login.newsTitle') }}
            </v-card-title>
            <v-card-text class="pa-0">
              <v-progress-linear value="100" color="secondary" rounded class="mb-3"></v-progress-linear>
              <p class="body-1 primary--text mb-5" v-html="$t('login.newsText')"></p>
              <div class="d-flex align-center">
                <v-btn rounded outlined small class="elevation-0 monospace-font white--text me-2" color="primary" :href="'https://www.driblab.com/blog/speed-and-endurance-two-new-driblab-scores-to-measure-physical-performance'" target="_blank">{{ $t('blog.readMore') }}</v-btn>
                <v-btn rounded small class="elevation-0 monospace-font white--text" color="primary" :href="'https://www.driblab.com/contact'" target="_blank">{{ $t('login.contactUs') }}</v-btn>
              </div>            
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>
    
    <v-footer fixed class="pt-1 pb-1" width='100%' color="brandDark" height="auto">
      <v-alert
        class="pa-4 mt-3 body-1"
        width="100%"
        dismissible>
        <span class="body-font">{{ $t('login.cookiesText') }}</span> 
        <v-btn text small exact href="https://policies.google.com/technologies/cookies?hl=en" target="_blank" color="primary" class="monospace-font h1">{{ $t('scoutingCalendar.moreInfo') }}</v-btn>
      </v-alert>
      <div class="d-flex align-center justify-space-between">
        <p class="monospace-font white--text mb-0 me-5">{{ `© ${currentYear} DRIBLAB. All rights reserved` }}</p>
        <v-btn text x-large class="monospace-font white--text" href="http://www.driblab.com/contact" target="_blank">{{ $t('login.contact') }}</v-btn>
      </div>
      <v-spacer></v-spacer>
      <!-- inse login social -->
    </v-footer>
    <ConfirmationModal
      @confirmation="onForceLoginConfirmation($event)"
      ref="confirmationForceModal"
      title="login.forceTitle"
      message="login.forceMessage"
    ></ConfirmationModal>
    <NewPassFormModal @save="onSaveNewPass($event)" ref="newPassFormModal"></NewPassFormModal>
    <ForgotPasswordModal @save="onForgotPasswordConfirm($event)" ref="forgotPasswordFormModal"></ForgotPasswordModal>
  </v-main>
</template>

<script>
import User from '../models/user';
import MaterialCardLogin  from './base/MaterialCardLogin';
import ConfirmationModal from './base/ConfirmationModal';
import NewPassFormModal from './base/NewPassFormModal';
import ForgotPasswordModal from './base/ForgotPasswordModal';
import UserService from "../services/user.service";
import AuthService from "../services/auth.service";
import BlogService from '../services/blog.service';
import { mdiLinkedin, mdiInstagram } from '@mdi/js';




export default {
  name: 'Login',
  components: {
    MaterialCardLogin,
    ConfirmationModal,
    NewPassFormModal,
    ForgotPasswordModal
  },
  data() {
    return {
      user: new User('', ''),
      loading: false,
      currentYear: new Date().getFullYear(),
      emailRules: [
        v => !!v || 'Username is required',
        // v => /.+@.+\..+/.test(v) || 'E-mail must be valid',
      ],
      passwordRules: [
        v => !!v || 'Password is required',
        // v => (v && v.length >= 8) || 'Password must be at least 8 characters',
      ],
       messageRules: [
        v => !!v || 'Message is required',
      ],

      message: '',
      slides: [1,2,3,4,5],
      entries: null,
      icons: [
        mdiLinkedin,
        mdiInstagram
      ],
      blogCardHeight: null,
      canLoadTwitter: false
    };
  },
  created() {
    //this.loadEntries();
  },
  computed: {
    loggedIn() {
      return this.$store.state.auth.status.loggedIn;
    },
    currentUser() {
      return this.$store.state.auth.user;
    },
    currentLanguage() {
      return this.$i18n.locale === 'es' || this.$i18n.locale === 'en' ? this.$i18n.locale : 'en';
    },
    entriesSliced(){
      if(this.entries){
        return this.entries.slice(0,3);
      }
      return [];
    },
    showTwitter(){
      let timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      return !timezone.includes("Asia");
    }
  },
  methods: {
    async loadEntries() {
      let blogResponse = await BlogService.getAll(3);
      this.entries = blogResponse.data;
      setTimeout(() => {this.canLoadTwitter = true},50);
    },
    async postSearch(entry){

      const authorized = true;
      let metaInfo = JSON.stringify({
        name: entry[`title_${this.currentLanguage}`],
        link: entry[`link_${this.currentLanguage}`]
      });
      let category = "blog";
      let urlId = entry.id;
      let tool = "";
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const browser = navigator.userAgent;
      let searchResponse = await UserService.postUserSearch(category, tool, urlId, authorized, this.$route.path, timeZone, browser, metaInfo);
      UserService.putLastSearchRendered({category,tool,id:searchResponse.data})
    },

    handleLogin() {
      this.loading = true;
      this.validate().then(isValid => {
        if (!isValid) {
          this.loading = false;
          return;
        }

        if (this.user.username && this.user.password) {
          this.$store.dispatch('auth/login', this.user).then(
            (user) => {
              if(user === 'EXPIRED_ACCOUNT'){
                this.loading = false;
                this.message = "Expired Account - Contact Driblab"
                AuthService.saveUserHistory({
                  username: this.user.username,
                  userHistoryTypeId:4,
                  outcome: false
                })
              }else if(user != "SOMEONE_IS_LOGGED"){
                const pathToGo = localStorage.getItem('redirectPath');
                if (pathToGo && pathToGo !== "/login" && user.numLogins) {
                  AuthService.saveUserHistory({
                    username: this.user.username,
                    userHistoryTypeId:1,
                    outcome: true
                  })
                  this.$router.push(localStorage.getItem('redirectPath'))
                  localStorage.removeItem('redirectPath');
                } else if(user && !user.numLogins){
                  this.$refs.newPassFormModal.open();
                } else {
                  AuthService.saveUserHistory({
                    username: this.user.username,
                    userHistoryTypeId:1,
                    outcome: true
                  })
                  this.$store.dispatch('clearRecentSearches');
                  let path = this.getHomePath(user.roles);
                  this.$router.push(path);
                }
                this.$vuetify.theme.dark = this.currentUser.darkMode;
              }else{
                this.showForceModal()
              }
            },
            () => {
              this.loading = false;
              this.message = "Unsucessful login"
              AuthService.saveUserHistory({
                username: this.user.username,
                userHistoryTypeId:1,
                outcome: false
              })

            }
          );
        }
      });
    },
    validate () {
      return Promise.resolve(this.$refs.form.validate())
    },
    getHomePath(roles){
      if(roles.includes('ROLE_API') && !roles.includes('ROLE_EVENTING_DATA') && !roles.includes('ROLE_ADMIN')){
        return '/api';
      }
      return '/home';
    },
    showForceModal(){
      this.$refs.confirmationForceModal.open();
    },
    onForceLoginConfirmation(confirmation){
      if(confirmation){
        this.user.force = true;
        this.handleLogin();
      }
      this.$refs.confirmationForceModal.close();
      this.loading = false;
    },
    async onSaveNewPass(user){
      if(user){
        this.loading = true;
        user.email = this.currentUser.username;
        let userResponse = await UserService.updatePassword(user);
        if(userResponse.data){
          this.$store.dispatch('auth/firstLogin');
          AuthService.saveUserHistory({
                    username: this.user.username,
                    userHistoryTypeId:1,
                    outcome: true
                  })
          this.$router.push('/home');
        }else{
          this.$store.dispatch('auth/logout');
        }
        this.loading = false;
      }else{
        this.$store.dispatch('auth/logout');
      }
    },
    onForgotPasswordClick(){
      this.$refs.forgotPasswordFormModal.open();
    },
    async onForgotPasswordConfirm(user){
      if(user){
        this.loading = true;
        let passwordResponse = await AuthService.forgotPassword(user);
        this.$store.dispatch("changeToast", {
            showToast: true,
            text: "general.checkYourInbox",
            outlined: false,
            color: "success darken-1",
            icon: "check_circle"
          });

        this.loading = false;
      }
    },
    onBlogCardHeightChange(height){
      this.blogCardHeight = height;
    }

  },
  mounted() {
    this.$store.dispatch("clearGlobal");
  }
};
</script>

<style lang="scss">
.login-background {
  position: fixed;
  inset: 0;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  z-index: 0;
}

.login-background video {
  position: absolute;
  top: 50%;
  left: 50%;
  min-width: 100%;
  min-height: 100%;
  width: auto;
  height: auto;
  object-fit: cover;
  transform: translate(-50%, -50%);
  pointer-events: none;
  user-select: none;
  -webkit-user-select: none;
}
</style>



// WEBPACK FOOTER //
// src/views/Login.vue